import React from 'react';
import { useTranslation } from 'react-i18next';
import { getUserLocale } from 'i18n/getUserLocale';
import {
  Alert,
  Button,
  Card,
  Loader,
  MultipleChoice,
} from '@unmind/design-system';
import { ClockInverted } from 'icons';
import { useTheme } from 'styled-components';

import { tracking } from 'App/Tracking';
import { SessionDetails, parseTimeString, TimeZone } from 'Book/shared';
import { hasApolloErrorCode } from 'Shared/Errors';
import { useUserEntitlements } from 'hooks';
import {
  formatAvailableTimes,
  changeStep,
  getDateString,
  goToStep,
} from '../../lib';
import { useCreateAppointment } from '../../hooks';
import { Footer } from '../';
import { IConfirmDetailsProps } from './types';
import { Wrapper as StyledWrapper } from './ConfirmDetails.styles';

const Wrapper = ({
  children,
  step,
  dispatch,
  answers,
  errors,
  validateSubmission,
  hideFooter = false,
  eapCanBookAppointments,
}: {
  children: React.ReactNode;
  validateSubmission(): void;
  hideFooter: boolean;
  eapCanBookAppointments: boolean;
} & Pick<IConfirmDetailsProps, 'step' | 'dispatch' | 'answers' | 'errors'>) => {
  const { t } = useTranslation('book', {
    keyPrefix: 'service.form',
  });

  return (
    <>
      <StyledWrapper as="fieldset">{children}</StyledWrapper>

      {!hideFooter && (
        <Footer
          step={step}
          dispatch={dispatch}
          forwardCTA={{
            label: t('ctas.submit'),
            disabled:
              !answers.time || !!errors?.confirm || !eapCanBookAppointments,
            validate: validateSubmission,
          }}
        />
      )}
    </>
  );
};

export default ({
  details,
  answers,
  dispatch,
  step,
  formData,
  errors,
  service,
}: IConfirmDetailsProps) => {
  const { t } = useTranslation('book', {
    keyPrefix: 'service.form',
  });
  const theme = useTheme();
  const locale = getUserLocale();
  const {
    loading: entitlementsLoading,
    entitlements: { eapCanBookAppointments },
    error: entitlementsError,
  } = useUserEntitlements();
  const [createAppointment, { data, loading, error }] = useCreateAppointment(
    () => {
      dispatch([
        {
          type: 'details.appointmentId',
          value: data?.createAppointment?.id,
        },
        { type: 'details.submittedForm', value: true },
      ]);
      tracking.track('book_eap_form_submitted');
      changeStep({ step, direction: 'forward', dispatch });
    },
  );

  const validateSubmission = async () => {
    if (!eapCanBookAppointments) {
      return;
    }

    let errorString: string | undefined;
    if (!formData['2'].date) errorString = t('error');
    if (!answers.time) errorString = t('confirmDetails.errors.pickTime');

    if (errorString) {
      dispatch({
        type: 'errors.confirm',
        value: errorString,
      });

      return;
    }

    try {
      await createAppointment({
        variables: {
          counsellorId: formData['3'].counsellor,
          date: getDateString(formData['2'].date),
          time: parseTimeString(formData['4'].time) || '',
          serviceId: service.id,
          emailAddress: formData['0'].email,
          phoneNumber: formData['0'].phone,
          filters: formData['1'],
        },
      });
    } catch (e) {
      // The error is already in `error` from useCreateAppointment
      console.error(e);
    }
  };

  const wrapperProps = {
    validateSubmission,
    step,
    dispatch,
    answers,
    errors,
    hideFooter: !!loading || !!entitlementsLoading || !!error,
    eapCanBookAppointments,
  };

  if (loading || entitlementsLoading) {
    return (
      <Wrapper {...wrapperProps}>
        <Loader variant="text" text={t('confirmDetails.loading')} />
      </Wrapper>
    );
  }

  const { counsellor } = details || {};
  const { date } = formData['2'];

  if (!date || !counsellor?.bookingTimes || error || entitlementsError) {
    let errorMessage: string = t('createAppointment.error');
    if (error) {
      if (
        hasApolloErrorCode(error, 'WPO_APPOINTMENT_CREATION_FAILED_DUPLICATE')
      ) {
        errorMessage = t('confirmDetails.errors.duplicateAppointment');
      } else if (error.message.toLowerCase().includes('timeout')) {
        errorMessage = t('confirmDetails.errors.timeout');
      }
    }

    return (
      <Wrapper {...wrapperProps}>
        <Alert
          className="card error"
          title={errorMessage}
          variant="error"
          size="small"
        />

        <Button
          label={t('ctas.restart')}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();

            goToStep({ step: 0, dispatch });
          }}
          className="start-again"
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper {...wrapperProps}>
      {!eapCanBookAppointments && answers.time ? (
        <Alert
          className="card info"
          title={t('confirmDetails.bookingDisabled')}
          variant="info"
          size="small"
        />
      ) : null}

      <SessionDetails
        appointment={{ type: formData[1].appointmentMode }}
        counsellor={{
          name: `${details?.counsellor?.firstName} ${details?.counsellor?.lastName}`.trim(),
          picture: details?.counsellor?.picture,
        }}
        dates={{
          startDate: formData[2]?.date,
          startTime: formData[4]?.time,
        }}
        type={service}
      />

      <p className="appointment appt-times">
        <ClockInverted primaryColor={theme.colors.border.primary} />
        <span>{t('confirmDetails.times')}</span>
      </p>

      {errors?.confirm ? (
        <Alert
          className="card error"
          title={t('confirmDetails.errors.pickTime')}
          variant="error"
          size="small"
        />
      ) : (
        <></>
      )}

      <MultipleChoice
        data-testid="confirm-time-choice"
        className="time-choices"
        onSelected={(value: string) => {
          dispatch([
            {
              type: 'answers.4.time',
              value: `${value}:00`,
            },
            {
              type: 'errors.confirm',
              value: undefined,
            },
          ]);
        }}
        type="radio"
        options={
          formatAvailableTimes(locale, counsellor.bookingTimes?.timeSlots) || []
        }
      />

      <TimeZone
        tz={formData[1].timeZone}
        date={formData[2].date || new Date()}
      />

      <Card tone="nested" styling="no-border" size="small">
        <h3>{t('confirmDetails.cancellationPolicy.title')}</h3>
        <p className="policy-details">
          {t('confirmDetails.cancellationPolicy.policy')}
        </p>
      </Card>
    </Wrapper>
  );
};
